import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronLeft, ChevronRight, CreditCard, Wallet } from "lucide-react";
import { useGetCreditHistory } from "@/hooks/api/query/useGetCreditHistory";
import { useGetCreditsBalance } from "@/hooks/api/query/useGetCreditsBalance";
import { ACTION_DISPLAY_CONFIG, isBillableAction } from "@/constants/credit";
import type { CreditTransaction } from "@/api/types/credits";

const PAGE_SIZE = 10;

function getTransactionLabel(tx: CreditTransaction): string {
  if (tx.action_type && isBillableAction(tx.action_type)) {
    return ACTION_DISPLAY_CONFIG[tx.action_type].name;
  }
  return tx.description;
}

function getTransactionIcon(tx: CreditTransaction): string {
  if (tx.action_type && isBillableAction(tx.action_type)) {
    return ACTION_DISPLAY_CONFIG[tx.action_type].icon;
  }
  switch (tx.transaction_type) {
    case "bonus":
      return "🎁";
    case "purchase":
      return "💳";
    case "refund":
      return "↩️";
    default:
      return "🔹";
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("ro-RO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function TransactionAmountBadge({ amount }: { amount: number }) {
  if (amount > 0) {
    return (
      <Badge variant="outline" className="text-green-600 border-green-300">
        +{amount}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-red-600 border-red-300">
      {amount}
    </Badge>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-8 text-muted-foreground">
      <CreditCard className="h-10 w-10 mx-auto mb-3 opacity-50" />
      <p>Nu ai tranzacții de credite încă.</p>
    </div>
  );
}

export default function CreditHistory() {
  const [page, setPage] = useState(0);
  const offset = page * PAGE_SIZE;

  const { data: balanceData, isLoading: balanceLoading } =
    useGetCreditsBalance();
  const { data: historyData, isLoading: historyLoading } = useGetCreditHistory(
    PAGE_SIZE,
    offset
  );

  const transactions = historyData?.transactions ?? [];
  const total = historyData?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasPrev = page > 0;
  const hasNext = page < totalPages - 1;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            <CardTitle>Consum Credite</CardTitle>
          </div>
          {balanceLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <Badge variant="secondary" className="text-base px-3 py-1">
              {balanceData?.credits ?? 0} credite
            </Badge>
          )}
        </div>
        <CardDescription>
          Istoricul tranzacțiilor tale de credite
        </CardDescription>
      </CardHeader>
      <CardContent>
        {historyLoading ? (
          <LoadingSkeleton />
        ) : transactions.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Acțiune</TableHead>
                    <TableHead>Companie</TableHead>
                    <TableHead className="text-right">Credite</TableHead>
                    <TableHead className="text-right">Sold</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap font-mono">
                        {formatDate(tx.created_at)}
                      </TableCell>
                      <TableCell>
                        <span className="mr-1.5">{getTransactionIcon(tx)}</span>
                        {getTransactionLabel(tx)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {tx.company_name ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <TransactionAmountBadge amount={tx.amount} />
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {tx.balance_after}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Pagina {page + 1} din {totalPages} ({total} tranzacții)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasPrev}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasNext}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Următor
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
